import React, { useState } from "react";
import "./CreativeFeedback.css";
import axiosInstance from "../../api/axiosInstance";
import { API_ENDPOINTS } from "../../api/config";

interface Props {
  onClose: () => void;
}

const CreativeFeedback: React.FC<Props> = ({ onClose }) => {
  const [satisfaction, setSatisfaction] = useState('');
  const [timely, setTimely] = useState('');
  const [professionalism, setProfessionalism] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!satisfaction || !timely || !professionalism) {
      setError('Please answer all questions before submitting.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axiosInstance.post(API_ENDPOINTS.feedback, {
        satisfaction: satisfaction.toLowerCase(),
        timely_service: timely.toLowerCase(),
        professionalism: professionalism.toLowerCase().replace(' ', '_'),
        remarks: remarks || '',
      });
      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="feedback-wrapper">
        <div className="feedback-success">
          <div className="feedback-success-icon">&#10003;</div>
          <h2 className="feedback-title">Thank You!</h2>
          <p className="feedback-subtitle">Your feedback has been submitted successfully.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-wrapper">
      <div className="feedback-header-section">
        <h2 className="feedback-title">We Value Your Feedback</h2>
        <p className="feedback-subtitle">Help us improve your experience</p>
      </div>

      <div className="feedback-body">
        {error && <div className="feedback-error">{error}</div>}

        <div className="question-block">
          <p className="question-label">1. How satisfied are you with our service?</p>
          <div className="radio-group">
            {['Satisfied', 'Neutral', 'Unsatisfied'].map((option) => (
              <label
                key={option}
                className={`radio-pill ${satisfaction === option ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="satisfaction"
                  value={option}
                  checked={satisfaction === option}
                  onChange={(e) => setSatisfaction(e.target.value)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="question-block">
          <p className="question-label">2. Was the service provided in a timely manner?</p>
          <div className="radio-group">
            {['Yes', 'No'].map((option) => (
              <label
                key={option}
                className={`radio-pill ${timely === option ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="timely"
                  value={option}
                  checked={timely === option}
                  onChange={(e) => setTimely(e.target.value)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="question-block">
          <p className="question-label">3. How would you rate the professionalism of our staff?</p>
          <div className="radio-group rating">
            {['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'].map((option) => (
              <label
                key={option}
                className={`radio-pill ${professionalism === option ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="professionalism"
                  value={option}
                  checked={professionalism === option}
                  onChange={(e) => setProfessionalism(e.target.value)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="question-block">
          <p className="question-label">4. Suggestions for improving our service:</p>
          <textarea
            className="feedback-textarea"
            placeholder="Share your thoughts with us..."
            rows={4}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </div>

        <div className="feedback-actions">
          <button
            className="btn-feedback-submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
          <button className="btn-feedback-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default CreativeFeedback;
